import React from "react";
import {
  ProfileContext,
  defaultProfile,
  Profile
} from "@velaro/velaro-component-lib";
import useApi from "../hooks/useApi";
import authService from "../services/AuthService";
import InitStuckBanner from "../components/InitStuckBanner";
import { useInitStuckDetector } from "../hooks/useInitStuckDetector";

// Seed from the token-exchange profile so the name/avatar shows immediately
// even before Profile/Me returns. The full fetch still runs to get teamIds,
// roles, status settings, etc.
function seedProfile(): Profile {
  const s = authService.profile as any;
  if (!s) return defaultProfile;
  const first = s.firstName || "";
  const last = s.lastName || "";
  const display = first && last
    ? `${first} ${last}`
    : first || s.displayName || s.email || s.userName || "";
  if (!first && !display) return defaultProfile;
  return {
    ...defaultProfile,
    picture: s.picture || "",
    firstName: first,
    lastName: last,
    displayName: display,
  };
}

export default function ProfileProvider(props: { children: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const [profile, setProfile] = React.useState<Profile>(seedProfile);
  const [fetchError, setFetchError] = React.useState(false);
  const api = useApi();
  const stuck = useInitStuckDetector(loading, 30000);

  React.useEffect(() => {
    async function load(isRetry = false) {
      let willRetry = false;
      try {
        const response = await api.messaging.get("Profile/Me");
        if (response.ok) {
          setProfile(await response.json());
        } else {
          console.error(`[ProfileProvider] Profile/Me returned ${response.status} — JWT mismatch or messaging API error. Name/avatar will use token-exchange data only.`);
          if (response.status !== 401) {
            if (!isRetry) {
              willRetry = true;
              setTimeout(() => load(true), 3000);
            } else {
              setFetchError(true);
            }
          }
        }
      } catch (err) {
        console.error("[ProfileProvider] Profile/Me fetch failed:", err);
        if (!isRetry) {
          willRetry = true;
          setTimeout(() => load(true), 3000);
        } else {
          setFetchError(true);
        }
      } finally {
        if (!willRetry) setLoading(false);
      }
    }

    load();
  }, [api]);

  const groupMap = React.useMemo(() => {
    const map: { [groupId: number]: boolean } = {};

    if (!profile.teamIds) {
      return map;
    }

    profile.teamIds.forEach((groupId) => {
      map[groupId] = true;
    });

    return map;
  }, [profile.teamIds]);

  const belongsToGroup = React.useCallback(
    (groupId: number) => {
      // null/undefined group id means all users can see.

      if (!groupId) {
        return true;
      }

      return groupMap[groupId] === true;
    },
    [groupMap]
  );

  if (stuck && loading) {
    return <InitStuckBanner message="Unable to load your profile. Please reload or sign out." />;
  }

  if (fetchError && !loading) {
    return <InitStuckBanner message="Unable to load your profile. Please reload or sign out." />;
  }

  return (
    <ProfileContext.Provider
      value={{ profile, loading, setProfile, belongsToGroup }}
    >
      {props.children}
    </ProfileContext.Provider>
  );
}
